import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Form, Button, Alert, Spinner, Modal } from 'react-bootstrap';
import { apiService } from '../../../services/api';
import InternationalPhoneInput from '../../common/InternationalPhoneInput';
import SkeletonLoader from '../../common/SkeletonLoader';
import './AutomaticSeatingForm.scss';

interface Cluster {
  id: number;
  name: string;
  description?: string;
  color: string;
  icon?: string;
  price_range: string;
}

interface AutomaticSeatingFormProps {
  show: boolean;
  event: any;
  user: any;
  onSuccess: (result: any) => void;
  onError: (error: string) => void;
  onCancel: () => void;
}

interface FormData {
  cluster_id: number | null;
  quantity: number;
  name: string;
  email: string;
  phone: string;
  custom_fields: Record<string, any>;
  additional_attendees: Array<{
    name: string;
    email?: string;
    phone?: string;
    custom_fields?: Record<string, any>;
  }>;
}

const AutomaticSeatingForm: React.FC<AutomaticSeatingFormProps> = ({
  show,
  event,
  user,
  onSuccess,
  onError,
  onCancel
}) => {
  const [clusters, setClusters] = useState<Cluster[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedCluster, setSelectedCluster] = useState<Cluster | null>(null);
  const [quota, setQuota] = useState(0);

  const [formData, setFormData] = useState<FormData>({
    cluster_id: null,
    quantity: 1,
    name: '',
    email: '',
    phone: '',
    custom_fields: {},
    additional_attendees: []
  });

  // Auto-fill form data when user is authenticated
  useEffect(() => {
    if (user && show) {
      const fullName = `${user.first_name} ${user.last_name}`.trim();
      const phoneNumber = user.profile?.phone_number || '';
      
      setFormData(prev => {
        // Only auto-fill if fields are empty
        if (!prev.name || !prev.email || !prev.phone) {
          return {
            ...prev,
            name: prev.name || fullName,
            email: prev.email || user.email,
            phone: prev.phone || phoneNumber,
          };
        }
        return prev;
      });
    }
  }, [user, show]);

  const loadClusters = async () => {
    try {
      setLoading(true);
      // Get clusters from event data
      if (event?.organized_categories?.clustered) {
        const clusterData = Object.values(event.organized_categories.clustered).map((cluster: any) => ({
          id: cluster.id,
          name: cluster.name,
          description: cluster.description,
          color: cluster.color || '#F23F39',
          icon: cluster.icon,
          price_range: cluster.price_range || 'Price varies'
        }));
        setClusters(clusterData);
      } else {
        // Fallback: try to get clusters from API if not in event data
        const response = await apiService.events.getEventDetail(event.slug);
        if (response.organized_categories?.clustered) {
          const clusterData = Object.values(response.organized_categories.clustered).map((cluster: any) => ({
            id: cluster.id,
            name: cluster.name,
            description: cluster.description,
            color: cluster.color || '#F23F39',
            icon: cluster.icon,
            price_range: cluster.price_range || 'Price varies'
          }));
          setClusters(clusterData);
        }
      }
    } catch (err: any) {
      console.error('Error loading clusters:', err);
      setError('Failed to load seating zones');
    } finally {
      setLoading(false);
    }
  };

  const calculateQuota = useCallback(() => {
    // Check if event or setting exists
    if (!event || !event.setting) {
      setQuota(10);
      return;
    }
    
    // Get limits - use the minimum of registration_limit and quota_limit
    const registrationLimit = event.setting.registration_limit || 10;
    const quotaLimit = event.setting.quota_limit || 10;
    
    // Take the minimum of both limits (most restrictive)
    let limit = Math.min(registrationLimit, quotaLimit);
    
    // Ensure minimum quota of 1 to allow at least one ticket
    const finalQuota = Math.max(1, limit);
    setQuota(finalQuota);
  }, [event, selectedCluster]);

  useEffect(() => {
    loadClusters();
  }, [event, user]);

  useEffect(() => {
    calculateQuota();
  }, [calculateQuota]);

  const handleClusterSelect = (cluster: Cluster) => {
    setSelectedCluster(cluster);
    setFormData(prev => ({
      ...prev,
      cluster_id: cluster.id
    }));
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCustomFieldChange = (fieldName: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      custom_fields: {
        ...prev.custom_fields,
        [fieldName]: value
      }
    }));
  };

  const handleAdditionalAttendeeChange = (index: number, field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      additional_attendees: prev.additional_attendees.map((attendee, i) => 
        i === index ? { ...attendee, [field]: value } : attendee
      )
    }));
  };

  const handleClearForm = () => {
    setFormData(prev => ({
      ...prev,
      name: '',
      email: '',
      phone: '',
      custom_fields: {},
      additional_attendees: prev.additional_attendees.map(attendee => ({
        ...attendee,
        name: '',
        email: '',
        phone: '',
        custom_fields: {}
      }))
    }));
    setError(null);
  };

  const handleQuantityChange = (quantity: number) => {
    setFormData(prev => {
      const newAdditionalAttendees = [];
      
      // Create additional attendee forms if needed
      if (quantity > 1 && event?.setting?.collect_individual_information) {
        for (let i = 1; i < quantity; i++) {
          newAdditionalAttendees.push({
            name: '',
            email: '',
            phone: '',
            custom_fields: {}
          });
        }
      }
      
      return {
        ...prev,
        quantity,
        additional_attendees: newAdditionalAttendees
      };
    });
  };

  const validateForm = (): boolean => {
    if (!formData.cluster_id) {
      setError('Please select a seating zone');
      return false;
    }
    
    if (!formData.name.trim()) {
      setError('Please enter your name');
      return false;
    }
    
    if (!formData.email.trim()) {
      setError('Please enter your email');
      return false;
    }
    
    if (!formData.phone.trim()) {
      setError('Please enter your phone number');
      return false;
    }
    
    if (formData.quantity > quota) {
      setError(`You can only select up to ${quota} ${quota === 1 ? 'ticket' : 'tickets'} for this event`);
      return false;
    }
    
    // Validate additional attendees if required
    if (event?.setting?.collect_individual_information && formData.quantity > 1) {
      for (let i = 0; i < formData.additional_attendees.length; i++) {
        const attendee = formData.additional_attendees[i];
        if (!attendee.name.trim()) {
          setError(`Please enter name for person ${i + 2}`);
          return false;
        }
      }
    }
    
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }
    
    try {
      setSubmitting(true);
      setError(null);
      
      const submitData = {
        event_slug: event.slug,
        cluster_id: formData.cluster_id!,
        quantity: formData.quantity,
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        custom_fields: formData.custom_fields,
        additional_attendees: formData.additional_attendees
      };
      
      console.log('Submitting cluster-seated registration with data:', submitData);
      console.log('Form data state:', formData);
      
      const result = await apiService.events.submitClusterSeatedRegistration(submitData);
      
      if (result.success) {
        // Redirect to cart page
        window.location.href = '/cart/';
      } else {
        setError(result.error || 'Registration failed');
      }
    } catch (err: any) {
      console.error('Registration error:', err);
      console.error('Error response:', err.response?.data);
      console.error('Error status:', err.response?.status);
      
      // Handle different types of errors
      if (err.response?.status === 422) {
        // Validation error - show detailed field errors
        const errorData = err.response.data;
        if (errorData.errors) {
          // Field-specific errors
          const fieldErrors = Object.entries(errorData.errors)
            .map(([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`)
            .join('; ');
          setError(`Validation error: ${fieldErrors}`);
        } else if (errorData.error) {
          setError(errorData.error);
        } else {
          setError('Please check your form data and try again');
        }
      } else {
        setError(err.response?.data?.error || err.message || 'Registration failed');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const nextStep = () => {
    if (currentStep === 1 && selectedCluster) {
      setCurrentStep(2);
    }
  };

  const prevStep = () => {
    if (currentStep === 2) {
      setCurrentStep(1);
    }
  };

  if (loading) {
    return (
      <Modal
        show={show}
        onHide={onCancel}
        size="xl"
        centered
        className="automatic-seating-modal"
      >
        <Modal.Body className="p-0">
          <div className="automatic-seating-form">
            <Row className="justify-content-center">
              <Col lg={12}>
                <div className="glass-card">
                  <div className="glass-header">
                    <div className="d-flex align-items-center">
                      <SkeletonLoader type="circle" width="48px" height="48px" className="me-3" />
                      <div style={{ flex: 1 }}>
                        <SkeletonLoader type="text" width="200px" height="24px" className="mb-2" />
                        <SkeletonLoader type="text" width="150px" height="18px" />
                      </div>
                    </div>
                    <div className="step-navigation mt-3">
                      <SkeletonLoader type="rect" width="120px" height="40px" className="me-2" />
                      <SkeletonLoader type="rect" width="120px" height="40px" />
                    </div>
                  </div>
                  <div className="glass-body">
                    <SkeletonLoader type="text" width="250px" height="28px" className="mb-2" />
                    <SkeletonLoader type="text" width="400px" height="18px" className="mb-4" />
                    <div className="cluster-grid">
                      {Array.from({ length: 3 }).map((_, index) => (
                        <div key={index} className="cluster-item-skeleton">
                          <SkeletonLoader type="circle" width="48px" height="48px" className="me-3" />
                          <div style={{ flex: 1 }}>
                            <SkeletonLoader type="text" width="70%" height="20px" className="mb-2" />
                            <SkeletonLoader type="text" width="50%" height="16px" className="mb-2" />
                            <SkeletonLoader type="text" width="100px" height="18px" />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </Col>
            </Row>
          </div>
        </Modal.Body>
      </Modal>
    );
  }

  return (
    <Modal
      show={show}
      onHide={onCancel}
      size="xl"
      centered
      className="automatic-seating-modal"
    >
      <Modal.Body className="p-0">
        <div className="automatic-seating-form">
            <Row className="justify-content-center">
              <Col lg={12}>
                <div className="glass-card">
              <div className="glass-header">
                <div className="d-flex align-items-center">
                  <div className="header-icon">
                    <i className="fa-solid fa-wand-magic-sparkles"></i>
                  </div>
                  <div className="header-content">
                    <h4 className="mb-1">Smart Seat Assignment</h4>
                    <p className="text-muted mb-0">{event.title}</p>
                  </div>
                </div>
                
                {/* Step Navigation */}
                <div className="step-navigation">
                  <div className={`step-item ${currentStep === 1 ? 'active' : ''}`}>
                    <span className="step-number">1</span>
                    <span className="step-text">Choose Zone</span>
                  </div>
                  <div className="step-divider"></div>
                  <div className={`step-item ${currentStep === 2 ? 'active' : ''}`}>
                    <span className="step-number">2</span>
                    <span className="step-text">Your Details</span>
                  </div>
                </div>
              </div>
              
              <div className="glass-body">
                {error && (
                  <Alert variant="danger" className="mb-3">
                    {error}
                  </Alert>
                )}

                {/* Step 1: Cluster Selection */}
                {currentStep === 1 && (
                  <div className="wizard-step active">
                    <div className="step-content">
                      <h5 className="step-title">Select Your Preferred Zone</h5>
                      <p className="step-description">Choose the zone where you'd like us to assign seats.</p>
                      
                      <div className="cluster-grid">
                        {clusters.map((cluster) => (
                          <div
                            key={cluster.id}
                            className={`cluster-item ${selectedCluster?.id === cluster.id ? 'selected' : ''}`}
                            onClick={() => handleClusterSelect(cluster)}
                          >
                            <div className="cluster-icon" style={{ '--cluster-color': cluster.color } as React.CSSProperties}>
                              {cluster.icon ? (
                                <i className={cluster.icon}></i>
                              ) : (
                                <i className="fas fa-map-marker-alt"></i>
                              )}
                            </div>
                            <div className="cluster-info">
                              <h6 className="cluster-name">{cluster.name}</h6>
                              {cluster.description && (
                                <p className="cluster-desc">{cluster.description}</p>
                              )}
                              <div className="cluster-details">
                                <span className="price-range">{cluster.price_range}</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    <div className="step-actions">
                      <Button
                        variant="primary"
                        onClick={nextStep}
                        disabled={!selectedCluster}
                        className="glass-btn"
                      >
                        Continue <i className="fas fa-arrow-right ms-2"></i>
                      </Button>
                    </div>
                  </div>
                )}

                {/* Step 2: Form Details */}
                {currentStep === 2 && (
                  <div className="wizard-step active">
                    <div className="step-content">
                      <h5 className="step-title">Complete Your Details</h5>
                      <p className="step-description">Provide your information to finish.</p>
                      
                      <div className="selected-cluster-info">
                        <div className="selected-cluster">
                          <i className="fas fa-check-circle text-success me-2"></i>
                          <span>{selectedCluster?.name}</span>
                        </div>
                      </div>
                      
                      <Form>
                        <Row>
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label>Number of Seats</Form.Label>
                              <Form.Select
                                value={formData.quantity}
                                onChange={(e) => handleQuantityChange(parseInt(e.target.value))}
                                required
                                className="simple-input"
                              >
                                {Array.from({ length: quota }, (_, i) => i + 1).map(num => (
                                  <option key={num} value={num}>{num}</option>
                                ))}
                              </Form.Select>
                              <Form.Text className="text-muted">
                                Maximum {quota} {quota === 1 ? 'ticket' : 'tickets'} available for this event
                              </Form.Text>
                            </Form.Group>
                          </Col>
                        </Row>
                        
                        <Row>
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label>Full Name</Form.Label>
                              <Form.Control
                                type="text"
                                value={formData.name}
                                onChange={(e) => handleInputChange('name', e.target.value)}
                                placeholder="Enter your full name"
                                required
                                className="simple-input"
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label>Email</Form.Label>
                              <Form.Control
                                type="email"
                                value={formData.email}
                                onChange={(e) => handleInputChange('email', e.target.value)}
                                placeholder="Enter your email"
                                required
                                className="simple-input"
                              />
                            </Form.Group>
                          </Col>
                        </Row>
                        
                        <Row>
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <InternationalPhoneInput
                                value={formData.phone}
                                onChange={(value) => handleInputChange('phone', value || '')}
                                placeholder="Enter your phone number"
                                id="phone"
                                name="phone"
                                label="Phone"
                                showLabel={true}
                                required={true}
                              />
                            </Form.Group>
                          </Col>
                        </Row>
                        
                        {/* Custom Fields */}
                        {event?.event_fields?.map((field: any) => {
                          return (
                            <Row key={field.id}>
                              <Col md={6}>
                                <Form.Group className="mb-3">
                                  <Form.Label>{field.label}</Form.Label>
                                  {field.field_type === 'select' ? (
                                    <Form.Select
                                      value={formData.custom_fields[field.name] || ''}
                                      onChange={(e) => handleCustomFieldChange(field.name, e.target.value)}
                                      required={field.required}
                                      className="simple-input"
                                    >
                                      <option value="">Select {field.label}</option>
                                      {Object.entries(field.options_dict || {}).map(([key, value]) => (
                                        <option key={key} value={value as string}>{key}</option>
                                      ))}
                                    </Form.Select>
                                  ) : (
                                    <Form.Control
                                      type={field.field_type}
                                      value={formData.custom_fields[field.name] || ''}
                                      onChange={(e) => handleCustomFieldChange(field.name, e.target.value)}
                                      placeholder={field.label}
                                      required={field.required}
                                      className="simple-input"
                                    />
                                  )}
                                </Form.Group>
                              </Col>
                            </Row>
                          );
                        })}
                        
                        {/* Additional Attendees */}
                        {event?.setting?.collect_individual_information && formData.quantity > 1 && (
                          <div className="additional-attendees-section">
                            <h6 className="mb-3">Additional Attendees</h6>
                            {formData.additional_attendees.map((attendee, index) => (
                              <div key={index} className="additional-attendee">
                                <h6 className="attendee-title">
                                  Person #{index + 2} Information
                                </h6>
                                <Row>
                                  <Col md={6}>
                                    <Form.Group className="mb-3">
                                      <Form.Label>Name</Form.Label>
                                      <Form.Control
                                        type="text"
                                        value={attendee.name}
                                        onChange={(e) => handleAdditionalAttendeeChange(index, 'name', e.target.value)}
                                        placeholder="Enter name"
                                        required
                                        className="simple-input"
                                      />
                                    </Form.Group>
                                  </Col>
                                  <Col md={6}>
                                    <Form.Group className="mb-3">
                                      <Form.Label>Email</Form.Label>
                                      <Form.Control
                                        type="email"
                                        value={attendee.email || ''}
                                        onChange={(e) => handleAdditionalAttendeeChange(index, 'email', e.target.value)}
                                        placeholder="Enter email"
                                        className="simple-input"
                                      />
                                    </Form.Group>
                                  </Col>
                                </Row>
                                <Row>
                                  <Col md={6}>
                                    <Form.Group className="mb-3">
                                      <InternationalPhoneInput
                                        value={attendee.phone || ''}
                                        onChange={(value) => handleAdditionalAttendeeChange(index, 'phone', value || '')}
                                        placeholder="Enter phone"
                                        id={`phone_${index + 2}`}
                                        name={`phone_${index + 2}`}
                                        label="Phone"
                                        showLabel={true}
                                        required={false}
                                      />
                                    </Form.Group>
                                  </Col>
                                </Row>
                                
                                {/* Custom Fields for Additional Attendees */}
                                {event?.event_fields?.map((field: any) => (
                                  <Row key={field.id}>
                                    <Col md={6}>
                                      <Form.Group className="mb-3">
                                        <Form.Label>{field.label}</Form.Label>
                                        {field.field_type === 'select' ? (
                                          <Form.Select
                                            value={attendee.custom_fields?.[field.name] || ''}
                                            onChange={(e) => {
                                              const newCustomFields = { ...attendee.custom_fields };
                                              newCustomFields[field.name] = e.target.value;
                                              handleAdditionalAttendeeChange(index, 'custom_fields', newCustomFields);
                                            }}
                                            required={field.required}
                                            className="simple-input"
                                          >
                                            <option value="">Select {field.label}</option>
                                            {Object.entries(field.options_dict || {}).map(([key, value]) => (
                                              <option key={key} value={value as string}>{key}</option>
                                            ))}
                                          </Form.Select>
                                        ) : (
                                          <Form.Control
                                            type={field.field_type}
                                            value={attendee.custom_fields?.[field.name] || ''}
                                            onChange={(e) => {
                                              const newCustomFields = { ...attendee.custom_fields };
                                              newCustomFields[field.name] = e.target.value;
                                              handleAdditionalAttendeeChange(index, 'custom_fields', newCustomFields);
                                            }}
                                            placeholder={field.label}
                                            required={field.required}
                                            className="simple-input"
                                          />
                                        )}
                                      </Form.Group>
                                    </Col>
                                  </Row>
                                ))}
                              </div>
                            ))}
                          </div>
                        )}
                      </Form>
                    </div>
                    
                    <div className="step-actions">
                      <Button
                        variant="outline-secondary"
                        onClick={prevStep}
                        className="me-2"
                      >
                        <i className="fas fa-arrow-left me-2"></i> Back
                      </Button>
                      <Button
                        variant="primary"
                        onClick={handleSubmit}
                        disabled={submitting}
                        className="glass-btn"
                      >
                        {submitting ? (
                          <>
                            <Spinner size="sm" className="me-2" />
                            Processing...
                          </>
                        ) : (
                          <>
                            <i className="fa-solid fa-wand-magic-sparkles me-2"></i>
                            {event?.content?.button_label || 'Assign My Seats'}
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
                </div>
              </Col>
            </Row>
   
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default AutomaticSeatingForm;
